import { useEffect, useState, useCallback } from 'react';
import { api } from '../../api';
import './Admin.css';

const STATUS_OPTIONS = ['new','in_review','approved','denied','adopted'];

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null);
  const [filter,    setFilter]    = useState('');
  const [saving,    setSaving]    = useState(false);
  const [notes,     setNotes]     = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.admin.getInquiries(filter ? `status=${filter}` : '')
      .then(setInquiries)
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = inq => {
    setSelected(inq);
    setNotes(inq.admin_notes || '');
  };

  const handleUpdate = async (id, updates) => {
    setSaving(true);
    try {
      const updated = await api.admin.updateInquiry(id, updates);
      setInquiries(prev => prev.map(i => i.id === id ? { ...i, ...updated } : i));
      if (selected?.id === id) setSelected(s => ({ ...s, ...updated }));
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = () => handleUpdate(selected.id, { admin_notes: notes, status: selected.status });

  const handleDelete = async id => {
    if (!window.confirm('Delete this inquiry permanently?')) return;
    try {
      await api.admin.deleteInquiry(id);
      setInquiries(prev => prev.filter(i => i.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (err) {
      alert(err.message);
    }
  };

  const statusColor = s => ({
    new:'badge-new', in_review:'badge-pending', approved:'badge-available',
    denied:'badge-pending', adopted:'badge-adopted',
  }[s] || 'badge-pending');

  const boolDisplay = v => v === 1 ? 'Yes' : v === 0 ? 'No' : '—';

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div>
          <h1 className="admin-page__title">Inquiries</h1>
          <p className="admin-page__subtitle">{inquiries.length} inquiries</p>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap:24 }}>
        {/* List */}
        <div className="admin-section">
          <div className="admin-filter-bar">
            <select value={filter} onChange={e => setFilter(e.target.value)}>
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : inquiries.length === 0 ? (
            <div className="admin-empty">
              <div className="admin-empty__icon">📋</div>
              <h3>No inquiries yet</h3>
              <p>Adoption inquiries submitted on the website will appear here.</p>
            </div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr><th>Applicant</th><th>Dog</th><th>Status</th><th>Date</th><th></th></tr>
                </thead>
                <tbody>
                  {inquiries.map(inq => (
                    <tr key={inq.id} style={{ cursor:'pointer', background: selected?.id === inq.id ? '#f0f7ff' : '' }}
                        onClick={() => openDetail(inq)}>
                      <td>
                        <strong>{inq.first_name} {inq.last_name}</strong>
                        <br /><small>{inq.email}</small>
                      </td>
                      <td>{inq.dog_name || <em style={{ color:'var(--text-light)' }}>General</em>}</td>
                      <td><span className={`badge ${statusColor(inq.status)}`}>{inq.status?.replace('_',' ')}</span></td>
                      <td style={{ whiteSpace:'nowrap' }}>{new Date(inq.created_at).toLocaleDateString()}</td>
                      <td><button className="btn-icon" onClick={e => { e.stopPropagation(); openDetail(inq); }}>View</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="admin-section" style={{ height:'fit-content', position:'sticky', top:24 }}>
            <div className="admin-section__header">
              <h2>{selected.first_name} {selected.last_name}</h2>
              <button className="admin-modal__close" onClick={() => setSelected(null)}>×</button>
            </div>
            <div style={{ padding:24 }}>
              {/* Applicant info */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:20, fontSize:'0.875rem' }}>
                {[
                  ['Email',    <a href={`mailto:${selected.email}`}>{selected.email}</a>],
                  ['Phone',    selected.phone || '—'],
                  ['Dog',      selected.dog_name || 'General inquiry'],
                  ['Housing',  selected.housing_type || '—'],
                  ['Has yard', boolDisplay(selected.has_yard)],
                  ['Other pets', selected.has_other_pets === 1 ? (selected.other_pets_desc || 'Yes') : boolDisplay(selected.has_other_pets)],
                  ['Children', selected.has_children === 1 ? (selected.children_ages ? `Yes (${selected.children_ages})` : 'Yes') : boolDisplay(selected.has_children)],
                  ['Submitted', new Date(selected.created_at).toLocaleDateString()],
                ].map(([label, val]) => (
                  <div key={label}>
                    <div style={{ fontFamily:'var(--font-ui)', fontSize:'0.7rem', textTransform:'uppercase', letterSpacing:'0.1em', color:'var(--text-light)', marginBottom:2 }}>{label}</div>
                    <div style={{ fontWeight:600, color:'var(--text)' }}>{val}</div>
                  </div>
                ))}
              </div>

              <div style={{ background:'var(--cream)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:16, marginBottom:20 }}>
                <div style={{ fontFamily:'var(--font-ui)', fontSize:'0.7rem', textTransform:'uppercase', letterSpacing:'0.1em', color:'var(--text-light)', marginBottom:8 }}>Message</div>
                <p style={{ fontSize:'0.875rem', lineHeight:1.7, color:'var(--text)' }}>{selected.message}</p>
              </div>

              {/* Status update */}
              <div className="form-group">
                <label className="form-label">Update Status</label>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {STATUS_OPTIONS.map(s => (
                    <button
                      key={s}
                      className={`pill ${selected.status === s ? 'pill--active' : ''}`}
                      onClick={() => handleUpdate(selected.id, { status: s, admin_notes: notes })}
                      disabled={saving}
                    >
                      {s.replace('_',' ')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="form-group">
                <label className="form-label">Internal Notes</label>
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder="Notes visible only to admins..."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
              </div>

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <button className="btn btn-primary" onClick={handleSaveNotes} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Notes'}
                </button>
                <button
                  className="btn-icon btn-icon--danger"
                  onClick={() => handleDelete(selected.id)}
                  style={{ padding:'8px 14px' }}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
